import { IDataTableColumn, showError } from "nsitools-react";
import * as React from "react";
import { css } from "styled-components";

import { TranslationDto, TranslationsApi, TranslationSearch } from "../../api";
import { SearchTablePage } from "../../components";
import { useApiService, useTl } from "../../hooks";
import { ETLCodes } from "../../locales";
import { TranslationTextEditor } from "./TranslationTextEditor";
import { exportFile } from "../../utils";
import { Dialog, Button } from "@blueprintjs/core";
import { FilePond } from "react-filepond";

export interface ITranslationEditPageProps {}

export const TranslationEditPage: React.FunctionComponent<ITranslationEditPageProps> = (props) => {
  const api = useApiService(TranslationsApi);
  const { t } = useTl();
  const [uploadedFiles, setUploadedFile] = React.useState<any[]>([]);
  const [isImporting, setIsImporting] = React.useState(false);
  const importTranslationFile = React.useCallback(async () => {
    try {
      setIsImporting(true);

      if (uploadedFiles.length > 0) {
        await api.upload({ data: uploadedFiles[0].file, fileName: uploadedFiles[0].filename });
      }
      setImportDialogOpened(false);
    } catch (err) {
      console.error(err);
      showError(t(ETLCodes.ErrorWhileImportingTranslations));
    }
    setIsImporting(false);
  }, [api, t, uploadedFiles]);
  const columns = React.useMemo<IDataTableColumn[]>(
    () => [
      {
        fieldName: "translationCode",
        header: () => t(ETLCodes.TranslationCode),
      },
      {
        fieldName: "languageCode",
        header: () => t(ETLCodes.LanguageCode),
      },
      {
        fieldName: "text",
        width: 5000,
        header: () => t(ETLCodes.Text),
        customizeCellStyle: () =>
          css({
            padding: "2px !important",
          }),
        render: (data: TranslationDto) => (
          <TranslationTextEditor
  initialTextValue={data.text}
  translationId={data.translationId}
  />
        ),
      },
    ],
    [t]
  );

  const search = React.useCallback((sObj) => api.baseSearch(TranslationSearch.fromJS(sObj)), [api]);
  const exportExcel = React.useCallback(async () => {
    const fd = await api.export();
    exportFile(fd);
  }, [api]);

  const [importDialogOpened, setImportDialogOpened] = React.useState<boolean>();

  return (
    <>
      <SearchTablePage
  columns={columns}
  getCriteriasFunction={() => api.getSearchCriterias(true)}
  searchFunction={search}
  sortKeys={{ translationCode: "ASC" }}
  breadCrumbs={[{ text: t(ETLCodes.Translations) }]}
  overrideListValues={{ LanguageId: (i) => i.displayValue }}
  buttons={[
    {
      rightIcon: "export",
      text: t(ETLCodes.Export),
      onClick: exportExcel
    },
    {
      rightIcon: "import",
      text: t(ETLCodes.Import),
      onClick: () => setImportDialogOpened(true)
    }
  ]}
  />
      <Dialog
        isOpen={importDialogOpened}
        canEscapeKeyClose={true}
        canOutsideClickClose={true}
        onClose={() => {
          setImportDialogOpened(false);
        }}
      >
        <FilePond
  files={uploadedFiles}
  allowMultiple={false}
  onupdatefiles={(fileitems) => setUploadedFile(fileitems)}
  labelIdle={t(ETLCodes.FilePondDragOrSelect)}
  />
        <Button
  text={t(ETLCodes.General_Save)}
  onClick={importTranslationFile}
  loading={isImporting}
  disabled={uploadedFiles.length === 0}
  />
      </Dialog>
    </>
  );
};
